import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as moment from 'moment';
import { CloudServiceService } from '../cloud/cloud-service.service';

@Injectable({
	providedIn: 'root'
})
export class CommonService {
	public dateSource: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public endDateCheck = this.dateSource.asObservable();

	constructor(private readonly cloudService: CloudServiceService) {}

	/**
     * @description check if the studentis existing one or a new user.
     * @param {Array} data list of students present.
     * @param {String} id unique identifier for the student.
     */
	public checkforExistingStudent(data, id): object {
		let studentData;
		data.forEach((student) => {
			if (student.id === id && student.accepted_terms && student.accepted_terms === true) {
				studentData = student;
			}
		});
		return studentData;
	}

	/**
     * @description get the data from the collections in firestore and
     * format the data for populating the UI.
     * @param { Object }
     * @param { Object }
     */
	public formatStudentData(): object {
		return;
	}

	public setEndDate(value) {
		this.dateSource.next(value);
	}

	/**
     * @description This is for override the exisiting student doc
     * @param user (Object) This is a incoming object
     * @param subtasks (Array) All subtask infromation
     * @param userAttrs (Object)
     */
	public overridesFor(user, subtasks, userAttrs) {
		userAttrs['multiplier'] = 1;
		userAttrs['user_subtasks'] = [];

		// Default start or end dates if not provided
		if (!userAttrs.hasOwnProperty('start_date')) {
			userAttrs['start_date'] = user.start_date;
		}
		if (!userAttrs.hasOwnProperty('end_date')) {
			userAttrs['end_date'] = user.end_date;
		}
		if (!userAttrs.hasOwnProperty('start_date')) {
			userAttrs['start_date'] = user.start_date;
		}
		if (!userAttrs.hasOwnProperty('total_duration_avg')) {
			userAttrs['total_duration_avg'] = user.total_duration_avg;
		}

		if (userAttrs['start_date'] && userAttrs['end_date']) {
			const start = moment(userAttrs['start_date'], 'YYYY-MM-DD');
			const end = moment(userAttrs['end_date'], 'YYYY-MM-DD');
			const duration: any = moment.duration(end.diff(start)).asDays().toFixed();

			userAttrs['multiplier'] = (duration / userAttrs['total_duration_avg']).toFixed(2);
		}

		userAttrs['user_subtasks'] = this.userSubtasksFor(user, subtasks, userAttrs['multiplier']);
		userAttrs['total_duration'] = userAttrs['user_subtasks'].reduce((prev, cur) => {
			return prev + parseInt(cur.time_to_complete);
		}, 0);

		return userAttrs;
	}

	/**
     * @description this will prepare the user_subtasks filed.
     * @param user (Object)
     * @param subtasks (Array)
     * @param multiplier (string)
     */
	public userSubtasksFor(user, subtasks, multiplier) {
		let timeDueFromStart = 0;
		const userSubtaskOverrides = [];

		subtasks.forEach((subtask) => {
			let userSubtaskAttrs = {};

			if (user.user_subtasks && user.user_subtasks.length > 0) {
				// existing user, needs overrides updated

				const userSubtask = user.user_subtasks.find((ust) => {
					return ust.subtask_id === subtask._id; // important to not be strict comparison here when dealing w/ mongo _ids
				});

				if (userSubtask) {
					userSubtaskAttrs = Object.assign({}, userSubtask);
					userSubtaskAttrs['time_to_complete'] = this.getComputedTimeToComplete(subtask, multiplier);
				} else {
					// new subtask
					userSubtaskAttrs = {
						subtask_id: subtask._id,
						time_to_complete: this.getComputedTimeToComplete(subtask, multiplier),
						activity: [],
						is_complete: false
					};
				}
			} else {
				// initial defaults / new subtask
				userSubtaskAttrs = {
					subtask_id: subtask._id,
					time_to_complete: this.getComputedTimeToComplete(subtask, multiplier),
					activity: [],
					is_complete: false
				};
			}

			timeDueFromStart += parseInt(userSubtaskAttrs['time_to_complete']);
			userSubtaskAttrs['time_due_from_start'] = timeDueFromStart;

			userSubtaskOverrides.push(userSubtaskAttrs);
		});

		return userSubtaskOverrides;
	}

	/**
     * @description will return timeToComplete field for each subtask
     * @param subtask (Object) Each subtask information
     * @param multiplier (String)
     */
	public getComputedTimeToComplete(subtask, multiplier) {
		let timeToComplete = subtask.time_to_complete_avg;
		const computedTimeToComplete = (subtask.time_to_complete_avg * multiplier).toFixed();

		// ensure at least minimum (or equal to)
		if (computedTimeToComplete >= subtask.time_to_complete_min) {
			timeToComplete = computedTimeToComplete;
		}

		// if it's less than the min range, use that number.
		if (computedTimeToComplete < subtask.time_to_complete_min) {
			timeToComplete = subtask.time_to_complete_min;
		}

		// ensure not higher than maximum
		if (computedTimeToComplete > subtask.time_to_complete_max) {
			timeToComplete = subtask.time_to_complete_max;
		}

		return parseInt(timeToComplete);
	}

	/**
     * @description will return progress
     * @param subtasks
     */
	public percentComplete(subtasks) {
		if (this.completedUnitsFor(subtasks) === 0) {
			return 0;
		}
		const percentAsInt = Math.round(this.completedUnitsFor(subtasks) / this.totalUnitsFor(subtasks) * 100);
		if (percentAsInt > 100) {
			return 100;
		} else {
			return percentAsInt;
		}
	}

	/**
     * @description total complete units
     * @param subtasks
     */
	public completedUnitsFor(subtasks) {
		return subtasks.filter((st) => st.is_complete).reduce((sum, subtask) => {
			if (!isNaN(parseInt(subtask.time_to_complete))) {
				return sum + parseInt(subtask.time_to_complete);
			} else {
				return sum + 0;
			}
		}, 0);
	}

	/**
     * @description return total units
     * @param subtasks
     */
	public totalUnitsFor(subtasks) {
		return subtasks.reduce((sum, subtask) => {
			if (!isNaN(parseInt(subtask.time_to_complete))) {
				return sum + parseInt(subtask.time_to_complete);
			} else {
				return sum + 0;
			}
		}, 0);
	}

	/**
     * @param student
     * @param subtasks
     */
	public isBehind(student, subtasks) {
		return this.paceInDays(student, subtasks) < -13;
	}

	public pacingText(student, subtasks) {
		if (this.isBehind(student, subtasks)) {
			return 'Behind Goal';
		} else {
			return 'On Track for Goal';
		}
	}

	/**
     * @param student 
     * @param subtasks 
     */
	public paceInDays(student, subtasks) {
		if (!student.end_date) {
			return 0;
		}
		const expectedPace = moment(student.end_date).subtract(
			this.totalUnitsFor(subtasks) - this.completedUnitsFor(subtasks),
			'days'
		);
		const difference = expectedPace.diff(moment(), 'days');
		return difference;
	}

	public disabledStartDates(student) {
		if (!student.end_date) {
			return {};
		} // else
		return {
			to: moment(student.end_date).subtract(student.total_duration_max, 'days').toDate(),
			from: moment(student.end_date).subtract(student.total_duration_min, 'days').toDate()
		};
	}

	public addtoStack(userId: string, event: string, logType: string, description) {
		const messageBody = description;
		this.cloudService.logToStack(
			userId,
			event,
			logType,
			messageBody,
			(response: object) => {
				// Response callback
			},
			(error: object) => {
				console.log(error);
			}
		);
	}

	public auditLog(email, userId, userType, activityStatus, payLoad, eventType) {
		const auditData = {
			email,
			userId,
			userType,
			activityStatus,
			payLoad,
			eventType
		};
		this.cloudService.postDataToBigQuery(
			auditData,
			(response) => {
				// Response callback
			},
			(error) => {
				console.log(error);
			}
		);
	}
}
